import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { isArray, get } from 'lodash';
import { useSelector } from 'react-redux';
import { matchPath, NavLink, useHistory, useLocation } from 'react-router-dom';
import { fromJS } from 'immutable';
import mixpanel from 'mixpanel-browser';
import { PhoneNumberUtil, PhoneNumberFormat } from 'google-libphonenumber';
import styled from 'styled-components';

// COMPONENTS
import CollapsibleList from 'components/CollapsibleList';
import DropdownMenu from 'components/DropdownMenu';
import TreeSelect from 'components/TreeSelect';

// HOOKS
import useStrategy, { FeatureFlags } from 'hooks/useStrategy';
import { useAppDispatch } from 'hooks/store';
import { useAbility } from 'permissions';
import { useHierarchy } from 'hooks';

// CONSTANTS
import { NAVIGATION_LINKS } from 'constants/navigation';
import { ANALYTICS_EVENTS } from 'constants/mixpanel.constants';

// SELECTORS
import { selectLocale } from 'services/Language/selectors';
import { selectUser } from 'services/Authentication/selectors';
import { selectDealershipSettings } from 'services/DealershipSettings/selectors';

// THUNKS
import { logoutUser } from 'services/Authentication/thunks';
import { persistUserLocale } from 'services/Language/thunks';

// RESOURCES
import { AuthUser, BrandingTypes } from 'services/resources';

// UTILS
import { historyPush } from 'utils/historyPush.utils';

// STYLES
import colors from 'styles/colors';
import {
  Drawer,
  DrawerBody,
  DrawerTitle,
  DrawerHead,
  DrawerSubtitle,
  DrawerFooter,
  LinkContainer,
  LinkPill,
  MenuContainer,
  MenuBar,
  MainMenuLogo,
  Navigation,
  NavItems,
  IconItemButton,
  SecondaryNavItems,
  SubmenuContainer,
  SubmenuItem,
  SubmenuLinkItem,
  MainMenuLogoIcon,
  ThirdPartySupportTextContainer,
} from './styles';
import { Clearfix } from 'styles/common';
import { NavMenuCloseButton, NavMenuItem, NavMenuLinkItem, NavMenuSeparator } from 'styles/navigation';

const phoneUtil = PhoneNumberUtil.getInstance();

type SubmenuItemsProps = {
  user: AuthUser;
  productName: string;
  onRedirect: (path: string) => void;
};

// Used for the left side navigation
const SubmenuItems = (props: SubmenuItemsProps) => {
  const { onRedirect } = props;
  const ability = useAbility();
  const dashboardEnabled = useStrategy(FeatureFlags.DASHBOARD_VIEW);

  const navLinks = dashboardEnabled
    ? NAVIGATION_LINKS
    : NAVIGATION_LINKS.map((link) => ({
        ...link,
        services: link.services.filter((service) => service.name !== 'dashboard'),
      }));

  const product = navLinks.find((item) => item.name === props.productName);

  if (!product) {
    return null;
  }

  const services = product.services
    .filter((service) => {
      if (typeof service.featureId !== 'undefined') {
        // TODO: Fix this. Breaks rule of Hooks.
        // eslint-disable-next-line react-hooks/rules-of-hooks
        return useStrategy(service.featureId);
      }
      return true;
    })
    .filter((service) => ability.can('view', service.name))
    .map((service) => {
      if (isArray(service.to)) {
        const dropdownItems = fromJS(
          service.to.map((item) => ({
            id: item.name,
            handleClick: onRedirect(isArray(item.to) ? '/' : item.to),
            renderedValue: { translationId: item.translationId },
          }))
        );

        return (
          <LinkContainer>
            <SubmenuItem key={service.translationId}>
              <DropdownMenu renderedValue={{ translationId: service.translationId }} items={dropdownItems} />
            </SubmenuItem>
            {service.isBeta && (
              <LinkPill>
                <FormattedMessage id={'beta'} />
              </LinkPill>
            )}
            {service.isNew && (
              <LinkPill>
                <FormattedMessage id={'new'} />
              </LinkPill>
            )}
          </LinkContainer>
        );
      }

      return (
        <LinkContainer key={service.translationId}>
          <SubmenuLinkItem>
            <NavLink to={service.to} activeStyle={{ color: colors.primary }}>
              <FormattedMessage id={service.translationId} />
            </NavLink>
          </SubmenuLinkItem>
          {service.isBeta && (
            <LinkPill>
              <FormattedMessage id={'beta'} />
            </LinkPill>
          )}
          {service.isNew && (
            <LinkPill>
              <FormattedMessage id={'new'} />
            </LinkPill>
          )}
        </LinkContainer>
      );
    });

  return <SubmenuContainer>{services}</SubmenuContainer>;
};

const ThirdPartySupport = () => {
  const dealershipSettings = useSelector(selectDealershipSettings());

  const brandingType = dealershipSettings.get('brandingType');

  const phoneNumber = dealershipSettings.get('inboundPhoneNumber');

  const shouldShow = dealershipSettings.get('inboundEmail') || phoneNumber;

  const parsedPhoneNumber = phoneNumber ? phoneUtil.parse(phoneNumber, 'US') : null;

  return shouldShow ? (
    <ThirdPartySupportTextContainer>
      <div>
        <FormattedMessage
          id={
            brandingType === BrandingTypes.AVIGO
              ? 'components.MainMenu.Avigo.supportText'
              : 'components.MainMenu.Weeve.supportText'
          }
        />
        {dealershipSettings.get('inboundPhoneNumber') && (
          <a href={`tel:${dealershipSettings.get('inboundPhoneNumber')}`}>
            {phoneUtil.format(parsedPhoneNumber, PhoneNumberFormat.INTERNATIONAL)}
          </a>
        )}
      </div>
      {dealershipSettings.get('inboundEmail') && (
        <a href={`mailto:${dealershipSettings.get('inboundEmail')}`}>{dealershipSettings.get('inboundEmail')}</a>
      )}
    </ThirdPartySupportTextContainer>
  ) : null;
};

const StyledHierarchyTree = styled.div`
  padding: 0 1em 1em 1em;

  & button {
    width: 100%;
  }
`;

const HierarchyTreeSelect = () => {
  const { hierarchy, selectHierarchyEntity, currentlySelectedEntity } = useHierarchy({ showAllEntitiesOptions: true });
  return (
    <StyledHierarchyTree>
      <label htmlFor="hierarchy-tree-select">
        <FormattedMessage defaultMessage={'Act Within'} id="hierarchy.treeSelect.label" />
      </label>
      <TreeSelect
        id="hierarchy-tree-select"
        initialValue={currentlySelectedEntity}
        options={hierarchy}
        onValueChange={selectHierarchyEntity}
      />
    </StyledHierarchyTree>
  );
};

const UserMenuPanel = (props) => {
  const user: AuthUser = useSelector(selectUser());
  const locale: string = useSelector(selectLocale());
  const dealershipSettings = useSelector(selectDealershipSettings());
  const { hasChildren } = useHierarchy();

  const AppListTitle = <FormattedMessage id="navigation.switchApps" />;
  const AppListTitleWrapper = NavMenuItem;

  const dispatch = useAppDispatch();

  const handleLanguageChange = (evt) => {
    const lang = evt.currentTarget.getAttribute('data-lang');

    if (user && (lang === 'en' || lang === 'fr')) {
      dispatch(persistUserLocale(user.profile.id, lang));
    }
  };

  const getBrandingText = () => {
    switch (dealershipSettings.brandingType) {
      case BrandingTypes.WEEVE:
        return 'apps.weeve';
      case BrandingTypes.AVIGO:
        return 'apps.avigo';
      default:
        return 'apps.fleet';
    }
  };

  return (
    <Drawer data-testid="dropdown-menu__content">
      <DrawerHead>
        <div>
          <DrawerTitle>{user?.firstName}</DrawerTitle>
          <DrawerSubtitle>{user?.profile.dealership.name}</DrawerSubtitle>
        </div>
        <NavMenuCloseButton className="icon-close-heavy" onClick={props.togglePanel} />
      </DrawerHead>
      <DrawerBody>
        {false && (
          <NavMenuItem>
            <FormattedMessage id="navigation.settings" />
          </NavMenuItem>
        )}
        {false && (
          // @ts-expect-error TS(2769): No overload matches this call.
          <CollapsibleList title={AppListTitle} TitleWrapper={AppListTitleWrapper}>
            <NavMenuLinkItem to="/fleet/vehicles">
              <FormattedMessage id={getBrandingText()} />
            </NavMenuLinkItem>
          </CollapsibleList>
        )}
        {hasChildren && (
          <>
            <HierarchyTreeSelect />
            <NavMenuSeparator />
          </>
        )}
        <NavMenuItem
          data-testid="main-menu__language-button"
          onClick={handleLanguageChange}
          data-lang={locale.startsWith('en') ? 'fr' : 'en'}
        >
          {locale.startsWith('en') && <FormattedMessage id="languages.french" />}
          {locale.startsWith('fr') && <FormattedMessage id="languages.english" />}
        </NavMenuItem>
        <NavMenuItem
          data-testid="main-menu__logout-button"
          onClick={() => {
            mixpanel.track(ANALYTICS_EVENTS.LOG_OUT);
            dispatch(logoutUser(true));
          }}
        >
          <FormattedMessage id="navigation.logout" />
        </NavMenuItem>
      </DrawerBody>
      {[BrandingTypes.WEEVE, BrandingTypes.AVIGO].includes(dealershipSettings.brandingType) && <ThirdPartySupport />}
      <DrawerFooter>v{__VERSION__}</DrawerFooter>
    </Drawer>
  );
};

type UserMenuProps = {
  user: AuthUser;
};

// Used for the logged in user menu
const UserMenu = (props: UserMenuProps) => {
  const [showPanel, setShowPanel] = useState(false);

  const togglePanel = () => setShowPanel(!showPanel);

  return (
    <>
      <IconItemButton key="0" data-testid="main-menu__account-actions-button" onClick={togglePanel}>
        <span>{props.user.firstName[0] + props.user.lastName[0]}</span>
      </IconItemButton>
      {showPanel && <UserMenuPanel key="1" user={props.user} togglePanel={togglePanel} />}
    </>
  );
};

const MainMenu = () => {
  const history = useHistory();
  const location = useLocation();
  const user: AuthUser = useSelector(selectUser());
  const dealershipSettings = useSelector(selectDealershipSettings());

  const handleRedirect = (link) => () => {
    historyPush(history, link);
  };

  if (!user) {
    return null;
  }

  const match = matchPath(location.pathname || '', {
    path: '/:product/',
  });

  const product = get(match, 'params.product');

  return (
    <MenuContainer>
      <MenuBar>
        <MainMenuLogo to="/">
          <MainMenuLogoIcon brandingType={dealershipSettings.brandingType} />
        </MainMenuLogo>
        <Navigation>
          {product && (
            <NavItems>
              <SubmenuItems user={user} productName={product} onRedirect={handleRedirect} />
            </NavItems>
          )}
          <SecondaryNavItems>
            {[BrandingTypes.WEEVE, BrandingTypes.AVIGO].includes(dealershipSettings.brandingType) && (
              <ThirdPartySupport />
            )}
            <UserMenu user={user} />
          </SecondaryNavItems>
        </Navigation>
        <Clearfix />
      </MenuBar>
    </MenuContainer>
  );
};

export default MainMenu;
